/**
 * DSP a new DOOH campaign of the active organization is created on: DOOH Direct (`perion`) for every
 * organization except the few the API keeps on the standard DSP (`mediasmart`). The API reports it as
 * `dooh_dsp_provider` in `GET /api/v1/campaign/options` and refuses a DOOH campaign sent to a DSP the
 * organization may not use (422).
 */
export type DoohDspProvider = "perion" | "mediasmart";

const DOOH_DSP_PROVIDERS: readonly DoohDspProvider[] = ["perion", "mediasmart"];

function isDoohDspProvider(value: unknown): value is DoohDspProvider {
    return DOOH_DSP_PROVIDERS.includes(value as DoohDspProvider);
}

/**
 * Reads `dooh_dsp_provider` from the campaign options: the default DSP of a new DOOH campaign. Only an
 * explicit `mediasmart` keeps the standard DSP: a missing field (options not loaded, older API) or any
 * other value means DOOH Direct, which is what every organization had before the exclusions existed.
 */
export function resolveDoohDspProvider(options: unknown): DoohDspProvider {
    const value = (options as { dooh_dsp_provider?: unknown } | null | undefined)?.dooh_dsp_provider;

    return value === "mediasmart" ? "mediasmart" : "perion";
}

/**
 * Reads `dooh_dsp_providers` from the campaign options: the DSPs a new DOOH campaign of the organization
 * may be created on, default first. Most organizations get one; a few may choose between DOOH Direct and
 * the standard DSP, and the create request then carries the one picked as `dsp_provider`.
 *
 * Anything that isn't a usable list falls back to `[resolveDoohDspProvider(options)]`, which is the
 * behaviour from before the field existed: a missing field (older API, options not loaded), no known
 * provider in it, or a list that leaves out the default `dooh_dsp_provider` (the two fields disagree,
 * and only the default is known to be accepted). Unknown entries and repeats are dropped, and the
 * default always goes first.
 */
export function resolveDoohDspProviders(options: unknown): DoohDspProvider[] {
    const defaultProvider = resolveDoohDspProvider(options);
    const raw = (options as { dooh_dsp_providers?: unknown } | null | undefined)?.dooh_dsp_providers;
    if (!Array.isArray(raw)) {
        return [defaultProvider];
    }

    const allowed = Array.from(new Set(raw.filter(isDoohDspProvider)));
    if (!allowed.includes(defaultProvider)) {
        return [defaultProvider];
    }

    return [defaultProvider, ...allowed.filter((provider) => provider !== defaultProvider)];
}
